/**
 * soundEffectsPollAdapter - Normalizes the sound effects job status response
 * into a generic `PollUpdate` consumed by GenerationContext.
 *
 * Note: The backend returns `progress` in the range 0.0-1.0; this adapter
 * converts it to 0-100 as expected by GenerationContext consumers.
 *
 * Self-registers on module load so that GenerationContext can resume
 * polling persisted sound effects jobs after a reload.
 */

import { SoundEffectsAPI } from '../soundEffectsAPI';
import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

export interface SoundEffectsJobResult {
  audioUrl: string;
  durationSeconds?: number;
  sampleRate?: number;
  format?: string;
  /** Present when the provider returns a unified audio+video output. */
  videoUrl?: string;
}

export const soundEffectsPollAdapter: PollAdapter = async (
  jobId: string
): Promise<PollUpdate<SoundEffectsJobResult>> => {
  let data;
  try {
    data = await SoundEffectsAPI.getSfxJobStatus(jobId);
  } catch (err) {
    return {
      done: true,
      status: 'failed',
      error: {
        message: err instanceof Error ? err.message : 'Failed to get sound effects status',
        sanitized: sanitizeBackendError(null),
      },
    };
  }

  const backendStatus = data.status;
  const isTerminal = backendStatus === 'completed' || backendStatus === 'failed';

  if (!isTerminal) {
    // Backend returns progress as 0.0-1.0; convert to 0-100.
    const progressPercent =
      typeof data.progress === 'number' ? Math.round(data.progress * 100) : undefined;
    return {
      done: false,
      status: 'running',
      progress: progressPercent,
      message: data.message,
    };
  }

  if (backendStatus === 'completed' && data.result?.audio_url) {
    return {
      done: true,
      status: 'completed',
      progress: 100,
      result: {
        audioUrl: data.result.audio_url,
        durationSeconds: data.result.duration_seconds,
        sampleRate: data.result.sample_rate,
        format: data.result.format,
        videoUrl: data.result.video_url,
      },
    };
  }

  return {
    done: true,
    status: 'failed',
    error: {
      message: data.error ?? 'Sound effect generation failed',
      sanitized: sanitizeBackendError(data),
    },
  };
};

registerPollAdapter('soundEffects', soundEffectsPollAdapter);
